import { atom, useAtom } from 'jotai';
import { MatrixClient, Method } from 'matrix-js-sdk';
import { atomFamily } from 'jotai/utils';
import { useCallback } from 'react';

// Archived room info type
export type RoomArchivedInfo = {
  archived: boolean;
};

// Atom family for archived status per room
export const roomArchivedInfoAtomFamily = atomFamily<string, RoomArchivedInfo | null>(
  () => atom<RoomArchivedInfo | null>(null)
);

// Atom for list of archived rooms (with pagination)
export type ArchivedRoomsList = {
  offset: number;
  rooms: any[];
  total_rooms: number;
  next_batch?: number;
  prev_batch?: number;
};

export const archivedRoomsListAtom = atom<ArchivedRoomsList | null>(null);

// Hook for fetching and setting archived status for a room
export const useRoomArchivedInfo = (mx: MatrixClient, roomId: string) => {
  const [archivedInfo, setArchivedInfo] = useAtom(roomArchivedInfoAtomFamily(roomId));

  // Fetch archived status for a room
  const fetchArchivedInfo = useCallback(async () => {
    try {
      const result: RoomArchivedInfo = await mx.http.authedRequest(
        Method.Get,
        `/rooms/${roomId}/archived`
      );
      setArchivedInfo(result);
      return result;
    } catch (error) {
      setArchivedInfo(null);
      return null;
    }
  }, [mx, roomId, setArchivedInfo]);

  // Set archived status for a room
  const setRoomArchived = useCallback(async (archived: boolean) => {
    const body = { archived };
    const result: RoomArchivedInfo = await mx.http.authedRequest(
      Method.Post,
      `/rooms/${roomId}/archived`,
      undefined,
      body
    );
    setArchivedInfo(result);
    return result;
  }, [mx, roomId, setArchivedInfo]);

  return { archivedInfo, fetchArchivedInfo, setRoomArchived };
};

// Hook for fetching the list of archived rooms (with pagination)
export const useArchivedRoomsList = (mx: MatrixClient) => {
  const [archivedRoomsList, setArchivedRoomsList] = useAtom(archivedRoomsListAtom);

  // Fetch archived rooms list
  const fetchArchivedRooms = useCallback(async (params?: {
    from?: number;
    limit?: number;
    order_by?: string;
    search_term?: string;
    dir?: string;
  }) => {
    const query = params
      ? '?' + Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`)
          .join('&')
      : '';
    try {
      const result: ArchivedRoomsList = await mx.http.authedRequest(
        Method.Get,
        `/rooms/archived${query}`
      );
      setArchivedRoomsList(result);
      return result;
    } catch (error) {
      setArchivedRoomsList(null);
      return null;
    }
  }, [mx, setArchivedRoomsList]);

  return { archivedRoomsList, fetchArchivedRooms };
};
